import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { FiXCircle, FiRefreshCw, FiInfo, FiCheck } from "react-icons/fi";
import CustomTextArea from "./CustomTextArea";
import CustomInput from "./CustomInput";

// Tạo constant để tránh re-create array mỗi lần render
const EMPTY_ARRAY = [];
const EMPTY_FUNCTION = () => {};

/**
 * ProjectInfoSection component - Phần "Thông tin dự án" sử dụng chung cho màn hình tạo bài viết
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const ProjectInfoSection = ({
  requirements,
  setRequirements,
  targetCustomer,
  setTargetCustomer,
  referenceUrl,
  setReferenceUrl,
  suggestedTopics = EMPTY_ARRAY,
  isSuggestingTopics = false,
  handleTopicSelect = EMPTY_FUNCTION,
  suggestTopics = EMPTY_FUNCTION,
  clearSuggestedTopics = EMPTY_FUNCTION,
  suggestedTopicsRef = null,
  isSuggestTopicsShown = true,
  domainConfig = null, // ✅ Nhận domainConfig từ parent thay vì gọi hook riêng
}) => {
  // State để track các chủ đề đã được chọn
  const [selectedTopics, setSelectedTopics] = useState(new Set());

  // ✅ FIXED: Use useMemo để đảm bảo labels được tính lại khi domainConfig thay đổi
  const labels = useMemo(() => {
    const result = domainConfig?.labels || {};
    return result;
  }, [domainConfig]);

  // Reset selected topics khi suggestedTopics thay đổi (ví dụ khi clear danh sách)
  useEffect(() => {
    if (suggestedTopics.length === 0) {
      setSelectedTopics((prev) => (prev.size > 0 ? new Set() : prev));
    }
  }, [suggestedTopics.length]); // ✅ Chỉ theo dõi length thay vì toàn bộ array để tránh re-run không cần thiết

  // Handler để chọn chủ đề và highlight
  const handleTopicClick = useCallback(
    (topic) => {
      // Gọi handler từ parent component
      handleTopicSelect(topic);

      // Cập nhật state để highlight chủ đề đã chọn
      setSelectedTopics((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(topic)) {
          newSet.delete(topic); // Nếu đã chọn thì bỏ chọn
        } else {
          newSet.add(topic); // Nếu chưa chọn thì thêm vào
        }
        return newSet;
      });
    },
    [handleTopicSelect]
  );

  // ✅ FIXED: Debug log khi domainConfig thay đổi (commented out to avoid console spam)
  // useEffect(() => {
  //   console.log("🔄 ProjectInfoSection: domainConfig changed:", {
  //     domainConfig,
  //     labels,
  //     projectInfoLabel: labels.projectInfoLabel,
  //     projectInfoPlaceholder: labels.projectInfoPlaceholder,
  //     targetCustomerLabel: labels.targetCustomerLabel,
  //     targetCustomerPlaceholder: labels.targetCustomerPlaceholder,
  //   });
  // }, [domainConfig]); // ✅ Chỉ theo dõi domainConfig, không theo dõi labels để tránh infinite loop

  return (
    <div className="space-y-6">
      {/* Thông báo chung ở đầu phần */}
      {/* <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start">
        <FiInfo className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          <span className="font-medium">Lưu ý:</span> Để AI có thể tạo nội dung,
          vui lòng cung cấp một trong ba nguồn: Yêu cầu nội dung, URL tham khảo
          hoặc Tài liệu tham khảo
        </p>
      </div> */}
      <div className="w-full">
        <div className="mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {labels.projectInfoLabel || "Yêu cầu nội dung"}
          </label>
        </div>
        <div className="relative w-full">
          <CustomTextArea
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            rows={6}
            placeholder={
              labels.projectInfoPlaceholder ||
              "Mô tả chi tiết về thông tin dự án..."
            }
            className={`w-full px-3 py-2 border ${
              requirements && requirements.trim() !== ""
                ? "border-gray-300"
                : "border-yellow-300"
            } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 placeholder:italic placeholder:text-gray-400 resize-none overflow-y-auto`}
          />
          {requirements && (
            <button
              onClick={() => setRequirements("")}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10"
              title="Xóa nội dung"
            >
              <FiXCircle size={20} />
            </button>
          )}
        </div>
        {isSuggestTopicsShown && (
          <div className="mt-2 flex justify-end">
            <button
              onClick={suggestTopics}
              disabled={isSuggestingTopics}
              className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md font-medium text-sm flex items-center"
            >
              {isSuggestingTopics ? (
                <>
                  <FiRefreshCw className="animate-spin mr-1" />
                  Đang gợi ý...
                </>
              ) : (
                "Gợi ý chủ đề"
              )}
            </button>
          </div>
        )}

        {/* Container cho danh sách chủ đề gợi ý - tách biệt khỏi textarea */}
        {suggestedTopics.length > 0 && (
          <div
            ref={suggestedTopicsRef}
            className="mt-4 mb-8 bg-white shadow-lg rounded-md border border-gray-200 w-full overflow-hidden"
          >
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
              <span className="font-medium text-gray-700 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Chủ đề gợi ý
              </span>
              <button
                onClick={clearSuggestedTopics}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {suggestedTopics.map((topic, index) => {
                const isSelected = selectedTopics.has(topic);
                return (
                  <div
                    key={index}
                    className={`px-4 py-3 cursor-pointer border-t border-gray-200 transition-all duration-200 flex items-center justify-between ${
                      isSelected
                        ? "bg-blue-100 border-blue-200 text-blue-800"
                        : "hover:bg-blue-50 text-gray-700"
                    }`}
                    onClick={() => handleTopicClick(topic)}
                  >
                    <span
                      className={`${
                        isSelected ? "font-medium" : ""
                      } break-words flex-1 pr-2`}
                    >
                      {topic}
                    </span>
                    {isSelected && (
                      <FiCheck
                        className="text-blue-600 ml-2 flex-shrink-0"
                        size={16}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="w-full">
        <label className="block text-gray-700 font-medium mb-2">
          {labels.targetCustomerLabel || "Khách hàng mục tiêu"}
        </label>
        <div className="relative w-full">
          <CustomTextArea
            value={targetCustomer}
            onChange={(e) => setTargetCustomer(e.target.value)}
            rows={3}
            placeholder={
              labels.targetCustomerPlaceholder ||
              "Mô tả đối tượng khách hàng mục tiêu..."
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 placeholder:italic placeholder:text-gray-400 resize-none overflow-y-auto"
          />
          {targetCustomer && (
            <button
              onClick={() => setTargetCustomer("")}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10"
              title="Xóa nội dung"
            >
              <FiXCircle size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="w-full">
        <div className="mb-1">
          <label className="block text-sm font-medium text-gray-700">
            URL tham khảo
          </label>
        </div>
        <div className="relative w-full">
          <CustomInput
            type="url"
            value={referenceUrl}
            onChange={(e) => {
              const value = e.target.value;
              setReferenceUrl(value);

              // Validate URL khi người dùng nhập
              if (value && value.trim() !== "") {
                try {
                  new URL(value);
                  // URL hợp lệ, xóa thông báo lỗi nếu có
                } catch (error) {
                  // URL không hợp lệ, có thể hiển thị thông báo hoặc styling khác
                  console.log("URL chưa hoàn chỉnh:", value);
                }
              }
            }}
            placeholder="Nhập URL bài viết để lấy thông tin tham khảo (ví dụ: https://example.com/article)"
            className="placeholder:italic placeholder:text-gray-400 w-full"
          />
          {referenceUrl && (
            <button
              onClick={() => setReferenceUrl("")}
              className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 z-10"
              title="Xóa URL"
            >
              <FiXCircle size={20} />
            </button>
          )}
        </div>
        {referenceUrl && referenceUrl.trim() !== "" && (
          <div className="mt-2 text-sm text-gray-600">
            <div className="flex items-start space-x-2">
              <span className="text-blue-500 font-medium">💡 Lưu ý:</span>
              <div className="flex-1">
                <p>• URL phải bắt đầu bằng http:// hoặc https://</p>
                <p>• Đảm bảo website có thể truy cập công khai</p>
                <p>• Website phải có nội dung có ý nghĩa để tham khảo</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProjectInfoSection);
